import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  useGetManagedUserAlertSummaryQuery,
  useUpdateManagedUserAlertSummaryMutation,
} from "../../redux/admin/adminApi";

const normalizeFilterValue = (value) => String(value || "").trim().toLowerCase();

const getDeviceId = (device) => String(device?._id || device?.id || "");
const toEntityId = (value) => String(value?._id || value?.id || value || "").trim();

const collectTargetUserIds = (targetUser) => {
  const ids = new Set();
  const pushId = (value) => {
    const id = toEntityId(value);
    if (id) ids.add(id);
  };

  pushId(targetUser?._id);
  pushId(targetUser?.assignedToUser);
  pushId(targetUser?.dealerOwner);
  pushId(targetUser?.subAdminOwner);
  pushId(targetUser?.createdBy);

  const managedUsers = Array.isArray(targetUser?.managedUsers) ? targetUser.managedUsers : [];
  managedUsers.forEach((user) => pushId(user));

  return ids;
};

export default function ManagedUserNotificationDialog({
  open,
  onClose,
  targetUser,
  devices = [],
  showStatus,
  title = "User Notifications",
}) {
  const [search, setSearch] = useState("");
  const [pendingKeys, setPendingKeys] = useState({});
  const [optimisticChecked, setOptimisticChecked] = useState({});
  const latestRequestRef = useRef({});

  const targetUserId = String(targetUser?._id || "");
  const targetScopeUserIds = useMemo(() => collectTargetUserIds(targetUser), [targetUser]);

  const assignedDevices = useMemo(() => {
    if (!targetUserId) return [];
    return (Array.isArray(devices) ? devices : []).filter((device) => {
      const adminOwnerId = toEntityId(device?.adminId);
      const dealerOwnerId = toEntityId(device?.dealer);
      const assignedToUserId = toEntityId(device?.assignedToUser);
      const createdByUserId = toEntityId(device?.createdBy);
      const subAdminOwnerId = toEntityId(device?.subAdminOwner);
      const dealerManagerId = toEntityId(device?.dealerOwner);
      const assignedViewers = Array.isArray(device?.assignedToViewers)
        ? device.assignedToViewers.map((viewer) => toEntityId(viewer))
        : [];
      return (
        targetScopeUserIds.has(adminOwnerId) ||
        targetScopeUserIds.has(dealerOwnerId) ||
        targetScopeUserIds.has(assignedToUserId) ||
        targetScopeUserIds.has(createdByUserId) ||
        targetScopeUserIds.has(subAdminOwnerId) ||
        targetScopeUserIds.has(dealerManagerId) ||
        assignedViewers.some((viewerId) => targetScopeUserIds.has(viewerId))
      );
    });
  }, [devices, targetScopeUserIds, targetUserId]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setPendingKeys({});
  }, [assignedDevices, open]);

  const {
    data: alertPayload,
    isFetching,
    refetch,
  } = useGetManagedUserAlertSummaryQuery(
    { userId: targetUserId, deviceIds: assignedDevices.map((device) => getDeviceId(device)).filter(Boolean) },
    { skip: !open || !targetUserId || assignedDevices.length === 0 }
  );
  const [updatePreferences] = useUpdateManagedUserAlertSummaryMutation();

  const rows = useMemo(() => {
    const base = Array.isArray(alertPayload?.alerts) ? alertPayload.alerts : [];
    const hydrated = base.map((item) => {
      const alarmKey = item?.alarmType || item?.key;
      if (!alarmKey || !Object.prototype.hasOwnProperty.call(optimisticChecked, alarmKey)) {
        return item;
      }
      return {
        ...item,
        checked: Boolean(optimisticChecked[alarmKey]),
        partiallyChecked: false,
      };
    });
    const query = normalizeFilterValue(search);
    if (!query) return hydrated;
    return hydrated.filter((item) => normalizeFilterValue(item?.label).includes(query));
  }, [alertPayload, optimisticChecked, search]);

  useEffect(() => {
    if (!open) {
      setOptimisticChecked({});
      latestRequestRef.current = {};
      return;
    }
  }, [open]);

  const handleToggle = async (item, checked) => {
    const assignedDeviceIds = assignedDevices.map((device) => getDeviceId(device)).filter(Boolean);
    if (!targetUserId || !assignedDeviceIds.length) return;

    const actionKey = item.key || item.alarmType;
    const requestId = (latestRequestRef.current[actionKey] || 0) + 1;
    latestRequestRef.current[actionKey] = requestId;
    const previousChecked = Boolean(item?.checked);

    setOptimisticChecked((prev) => ({
      ...prev,
      [item.alarmType]: Boolean(checked),
    }));
    setPendingKeys((prev) => ({
      ...prev,
      [actionKey]: true,
    }));
    try {
      await updatePreferences({
        userId: targetUserId,
        deviceIds: assignedDeviceIds,
        alarmType: item.alarmType,
        checked,
      }).unwrap();
      if (latestRequestRef.current[actionKey] === requestId) {
        setOptimisticChecked((prev) => {
          const next = { ...prev };
          delete next[item.alarmType];
          return next;
        });
        setPendingKeys((prev) => {
          const next = { ...prev };
          delete next[actionKey];
          return next;
        });
      }
      refetch();
      showStatus?.(
        "success",
        `${item.label} notification ${checked ? "assigned successfully" : "unassigned successfully"} for ${targetUser?.username || "user"}.`
      );
    } catch (error) {
      if (latestRequestRef.current[actionKey] === requestId) {
        setOptimisticChecked((prev) => ({
          ...prev,
          [item.alarmType]: previousChecked,
        }));
        setPendingKeys((prev) => {
          const next = { ...prev };
          delete next[actionKey];
          return next;
        });
      }
      showStatus?.("error", error?.data?.msg || "Failed to update user notification preference.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
    >
      <Box sx={{ px: 3, py: 2.5, bgcolor: "#fff", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontSize: { xs: 26, sm: 34 }, lineHeight: 1, fontWeight: 900, color: "#111" }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 14, color: "#6b7280", fontWeight: 600 }}>
            {targetUser?.username || targetUser?.name || "Selected user"}
          </Typography>
          {alertPayload?.ownerUser?.username ? (
            <Typography sx={{ mt: 0.5, fontSize: 13, color: "#6b7280", fontWeight: 600 }}>
              Templates controlled by {alertPayload.ownerUser.username}
            </Typography>
          ) : null}
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#111", mt: -0.5, mr: -1 }}>
          <CloseIcon sx={{ fontSize: 34 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "#f7f7f6", px: 3, py: 2.5 }}>
        <Typography sx={{ mb: 1.75, fontSize: 13, color: "#6b7280", fontWeight: 700 }}>
          Applies to {assignedDevices.length} assigned vehicle{assignedDevices.length === 1 ? "" : "s"}.
        </Typography>

        <TextField
          fullWidth
          placeholder="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              bgcolor: "#fff",
              borderRadius: 1.5,
              fontSize: 20,
              fontWeight: 700,
              pr: 1,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: "#ff5f7e", fontSize: 36 }} />
              </InputAdornment>
            ),
          }}
          disabled={!assignedDevices.length}
        />

        {!assignedDevices.length ? (
          <Typography sx={{ py: 3, textAlign: "center", color: "#6b7280", fontWeight: 600 }}>
            No assigned devices found for this user.
          </Typography>
        ) : isFetching && !rows.length ? (
          <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={0.75}>
            {rows.map((item) => {
              const saving = Boolean(pendingKeys[item.key || item.alarmType]);
              return (
                <Box
                  key={item.key}
                  sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 46 }}
                >
                  <Checkbox
                    checked={Boolean(item.checked)}
                    disabled={saving}
                    onChange={(event) => handleToggle(item, event.target.checked)}
                    sx={{
                      p: 0.5,
                      color: "#1976d2",
                      "&.Mui-checked": { color: "#1976d2" },
                    }}
                  />
                  <Typography sx={{ fontSize: 22, lineHeight: 1.2, color: "#1f2937", fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                  {item.inherited ? (
                    <Typography sx={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>
                      inherited
                    </Typography>
                  ) : null}
                  {saving ? <CircularProgress size={16} sx={{ ml: 0.75 }} /> : null}
                </Box>
              );
            })}

            {!rows.length ? (
              <Typography sx={{ py: 3, textAlign: "center", color: "#6b7280", fontWeight: 600 }}>
                No notification templates available for this role right now.
              </Typography>
            ) : null}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
